import axios from 'axios';
import { config } from '../config/env';

const API_URL = config.API_URL;

// Dedicated axios instance for admin
const adminApi = axios.create({
    baseURL: `${API_URL}/api/admin`,
    headers: {
        'Content-Type': 'application/json'
    },
    withCredentials: true
});

// Admin auth interceptor
adminApi.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('admin_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Admin response interceptor for 401s
adminApi.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_user');
            if (window.location.pathname.startsWith('/admin') && !window.location.pathname.includes('/admin/login')) {
                window.location.href = '/admin/login';
            }
        }
        return Promise.reject(error);
    }
);

export const adminAuthService = {
    login: async (email, password) => {
        const response = await adminApi.post('/auth/login', { email, password });
        if (response.data.token) {
            localStorage.setItem('admin_token', response.data.token);
            localStorage.setItem('admin_user', JSON.stringify(response.data.admin));
        }
        return response.data;
    },
    logout: async () => {
        await adminApi.post('/auth/logout');
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
    },
    getMe: async () => {
        const response = await adminApi.get('/auth/me');
        return response.data;
    }
};

export const adminManagementService = {
    // Dashboard
    getStats: (params) => adminApi.get('/stats', { params }).then(res => res.data),

    // AI Usage
    getAIUsageStats: (params) => adminApi.get('/ai-usage/stats', { params }).then(res => res.data),
    getAIUsageHistory: (params) => adminApi.get('/ai-usage/history', { params }).then(res => res.data),
    getTopAIWorkspaces: (params) => adminApi.get('/ai-usage/top-workspaces', { params }).then(res => res.data),
    getAIUsageLogs: (params) => adminApi.get('/ai-usage/logs', { params }).then(res => res.data),

    // Users
    getUsers: (params) => adminApi.get('/users', { params }).then(res => res.data),
    getUserDetail: (id) => adminApi.get(`/users/${id}`).then(res => res.data),
    exportUsers: (params) => adminApi.get('/users/export', { params, responseType: 'blob' }).then(res => res.data),
    updateUser: (id, data) => adminApi.put(`/users/${id}`, data).then(res => res.data),
    deleteUser: (id) => adminApi.delete(`/users/${id}`).then(res => res.data),

    // Workspaces
    getWorkspaces: (params) => adminApi.get('/workspaces', { params }).then(res => res.data),
    updateWorkspace: (id, data) => adminApi.put(`/workspaces/${id}`, data).then(res => res.data),
    deleteWorkspace: (id) => adminApi.delete(`/workspaces/${id}`).then(res => res.data),
    getWorkspaceDetail: (id) => adminApi.get(`/workspaces/${id}`).then(res => res.data),

    // Apps
    getApps: (params) => adminApi.get('/apps', { params }).then(res => res.data),
    getAppDetail: (id) => adminApi.get(`/apps/${id}`).then(res => res.data),

    // Subscriptions
    getSubscriptions: (params) => adminApi.get('/subscriptions', { params }).then(res => res.data),
    createSubscription: (workspaceId, data) => adminApi.post(`/workspaces/${workspaceId}/subscription`, data).then(res => res.data),
    updateSubscription: (id, data) => adminApi.put(`/subscriptions/${id}`, data).then(res => res.data),

    getAdmins: () => adminApi.get('/admins').then(res => res.data),
    createAdmin: (data) => adminApi.post('/admins', data).then(res => res.data),
    deleteAdmin: (id) => adminApi.delete(`/admins/${id}`).then(res => res.data),

    // Coupons
    getCoupons: (params) => adminApi.get('/coupons', { params }).then(res => res.data),
    createCoupon: (data) => adminApi.post('/coupons', data).then(res => res.data),
    updateCoupon: (id, data) => adminApi.put(`/coupons/${id}`, data).then(res => res.data),
    deleteCoupon: (id) => adminApi.delete(`/coupons/${id}`).then(res => res.data),
    toggleCouponActive: (id) => adminApi.post(`/coupons/${id}/toggle-active`).then(res => res.data),

    // Feature Flags
    getFeatureFlags: (params) => adminApi.get('/feature-flags', { params }).then(res => res.data),
    createFeatureFlag: (data) => adminApi.post('/feature-flags', data).then(res => res.data),
    updateFeatureFlag: (id, data) => adminApi.put(`/feature-flags/${id}`, data).then(res => res.data),
    deleteFeatureFlag: (id) => adminApi.delete(`/feature-flags/${id}`).then(res => res.data),

    // Test Goals & Test Runs
    getGoals: (params) => adminApi.get('/goals', { params }).then(res => res.data),
    getGoalDetail: (id) => adminApi.get(`/goals/${id}`).then(res => res.data),
    getRuns: (params) => adminApi.get('/runs', { params }).then(res => res.data),
    getRunDetail: (id) => adminApi.get(`/runs/${id}`).then(res => res.data)
};

export default adminApi;
